import { CartAddEvent, ThemeEvents } from '@theme/events';

const RECS_LIMIT = 6;
const MAX_SHOWN = 2;

/**
 * A custom element that renders a small "You may also like" cross-sell
 * inside the cart drawer, using Shopify's native product recommendations
 * endpoint based on the first item in the cart.
 *
 * Every cart update re-renders the drawer's section server-side and morphs
 * it in, which resets this element's light DOM back to its empty/hidden
 * server markup (the recommendations are only ever populated client-side).
 * Since morph patches this element in place rather than replacing it,
 * connectedCallback does not fire again on its own — so we explicitly
 * listen for cart updates and reload after each one.
 *
 * The reload is deferred with setTimeout rather than requestAnimationFrame:
 * rAF callbacks can be suppressed when the tab isn't actively rendering,
 * which would silently prevent the reload from ever running.
 */
class CartRecommendations extends HTMLElement {
  /** @type {boolean} */
  #loading = false;
  /** @type {boolean} */
  #pendingReload = false;

  connectedCallback() {
    this.#load();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Cart updates morph this element's content back to its pristine,
   * server-rendered (empty/hidden) state. Reload once the morph settles.
   */
  #onCartUpdate = () => {
    setTimeout(() => this.#load(), 0);
  };

  async #load() {
    if (this.#loading) {
      this.#pendingReload = true;
      return;
    }
    this.#loading = true;
    try {
      await this.#fetchAndRender();
    } finally {
      this.#loading = false;
      if (this.#pendingReload) {
        this.#pendingReload = false;
        this.#load();
      }
    }
  }

  async #fetchAndRender() {
    const productId = this.dataset.productId;
    const baseUrl = this.dataset.url;
    if (!productId || !baseUrl) return;

    try {
      const [recsResponse, cartResponse] = await Promise.all([
        fetch(`${baseUrl}?product_id=${productId}&limit=${RECS_LIMIT}&intent=related`),
        fetch('/cart.js'),
      ]);

      if (!recsResponse.ok) throw new Error('recommendations request failed');

      const recsData = await recsResponse.json();
      const cart = await cartResponse.json();
      const cartProductIds = new Set((cart.items || []).map((item) => item.product_id));

      const products = (recsData.products || [])
        .filter((product) => !cartProductIds.has(product.id))
        .slice(0, MAX_SHOWN);

      if (products.length === 0) {
        this.hidden = true;
        return;
      }

      this.#render(products);
      this.hidden = false;
    } catch (error) {
      console.error('Cart recommendations error:', error);
      this.hidden = true;
    }
  }

  /**
   * @param {Array<Object>} products
   */
  #render(products) {
    const list = this.querySelector('[data-recs-list]');
    if (!list) return;

    list.innerHTML = products.map((product) => this.#cardMarkup(product)).join('');

    list.querySelectorAll('[data-recs-add]').forEach((button) => {
      button.addEventListener('click', (event) => this.#handleAdd(event));
    });
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2).replace('.', ',');
    return `€${amount}`;
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  /**
   * @param {Object} product
   */
  #cardMarkup(product) {
    const variant = (product.variants && product.variants.find((v) => v.available)) || (product.variants && product.variants[0]);
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const price = this.#formatMoney(variant ? variant.price : product.price);
    const title = this.#escapeHtml(product.title);
    const available = variant ? variant.available : false;

    return `
      <div class="pilumo-cart-recs__card">
        <a href="${product.url}" class="pilumo-cart-recs__image-link" tabindex="-1">
          <img src="${image}" alt="${title}" width="72" height="72" loading="lazy" class="pilumo-cart-recs__image">
        </a>
        <div class="pilumo-cart-recs__info">
          <a href="${product.url}" class="pilumo-cart-recs__title">${title}</a>
          <button
            type="button"
            class="pilumo-cart-recs__add"
            data-recs-add
            data-variant-id="${variant ? variant.id : ''}"
            ${available ? '' : 'disabled'}
          >
            <span data-recs-add-label>Aggiungi &middot; ${price}</span>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * @param {MouseEvent} event
   */
  async #handleAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = button?.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    const label = button.querySelector('[data-recs-add-label]');
    const originalText = label ? label.textContent : '';
    if (label) label.textContent = 'Aggiunta...';

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    try {
      const addUrl = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });

      const addData = await addResponse.json();

      if (addData.status) {
        if (label) label.textContent = originalText;
        button.disabled = false;
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'cart-recommendations', {
          source: 'cart-recommendations',
          itemCount: cart.item_count,
          sections: addData.sections,
        })
      );
    } catch (error) {
      console.error('Cart recommendations add error:', error);
      if (label) label.textContent = originalText;
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
